import { useCallback, useMemo, useRef } from 'react';
import type {
  AudioAnalyticEvents,
  AudioPlaybackInfo,
  QuickRecallAnalyticEvents,
  QuickRecallMark,
  QuickRecallNavDirection,
  QuizAnalyticEvents,
  QuizAnswerEvent,
  QuizAttemptEvent,
  QuizStartEvent,
  ShareChannel,
  StandoutsAnalyticEvents,
  SummaryAnalyticEvents,
} from '@libertify/ui';
import { useAnalytics } from './useAnalytics';
import {
  MediaAnalyticsEvents,
  PodcastPlaybackProperties,
  ProjectType,
  isAddonKey,
} from '../types';

const toAnswerRecord = (a: QuizAnswerEvent) => ({
  question_index: a.questionIndex,
  pool_question_index: a.poolQuestionIndex,
  selected_option_indices: a.selectedOptionIndices,
  is_correct: a.isCorrect,
});

/** Common `podcast_*` payload from the player's snapshot; whole seconds. */
const toPodcastProperties = (
  info: AudioPlaybackInfo,
): PodcastPlaybackProperties => ({
  track: info.track,
  position_seconds: Math.round(info.positionSeconds),
  duration_seconds: Math.round(info.durationSeconds),
  listened_seconds: info.listenedSeconds,
  playback_rate: info.playbackRate,
});

/**
 * Segment analytics for Knowledge Hub add-ons.
 *
 * Client navigation + the quiz lifecycle (start / per-answer / attempt). The
 * apps pass these as callbacks to the presentational add-on components, which
 * own the interaction and emit the raw facts; this hook maps them to the typed
 * `web_experience` events (reusing the Quiz add-on's own `QuizAnswerEvent` /
 * `QuizAttemptEvent` shapes so the contract lives in one place).
 *
 * Lives in experience-viewer (home of the MediaAnalyticsEvents enum + its
 * coverage test) rather than in each app. Pass the publication identifiers so
 * every event carries project / publication / organization (and, where the
 * surface has no super property for it, workspace) context.
 */
export function useAddonAnalytics(props?: {
  projectType?: ProjectType;
  projectId?: string;
  publicationId?: string;
  organizationId?: string;
  /** See useAnalytics — only surfaces without a super property pass this. */
  workspaceId?: string;
}) {
  const { trackEvent } = useAnalytics(props);

  // The last tab this hook opened, so its close can be emitted when another
  // tab takes focus. Tabs have no close affordance — losing focus is the only
  // close — and pairing off the previous *open* keeps every close matched to
  // an open.
  const lastOpenedAddonRef = useRef<string | null>(null);

  /**
   * An add-on tab was opened (e.g. the viewer clicks the Quiz tab). Also
   * emits `addon_tab_close` for the tab that just lost focus, if any.
   */
  const trackAddonTabOpen = useCallback(
    (addon: string) => {
      if (!isAddonKey(addon)) return;
      const previous = lastOpenedAddonRef.current;
      if (previous && previous !== addon && isAddonKey(previous)) {
        trackEvent(MediaAnalyticsEvents.AddonTabClose, { addon: previous });
      }
      lastOpenedAddonRef.current = addon;
      trackEvent(MediaAnalyticsEvents.AddonTabOpen, { addon });
    },
    [trackEvent],
  );

  /**
   * The Knowledge Hub rendered with `addon` as its default tab. AntD Tabs
   * only reports clicks, so without this the default tab never opens. No-op
   * once any tab has opened, so an effect re-running it is harmless.
   */
  const trackInitialAddonTabOpen = useCallback(
    (addon: string) => {
      if (lastOpenedAddonRef.current) return;
      trackAddonTabOpen(addon);
    },
    [trackAddonTabOpen],
  );

  /**
   * A header CTA was clicked. Mirrors the classic experience-viewer's
   * CallToActionList (libs/experience-viewer/src/lib/components/CallToActions.tsx),
   * which fires this same event + payload shape on every CTA click.
   */
  const trackCtaClick = useCallback(
    (ctaType: string, ctaLabel: string) => {
      trackEvent(MediaAnalyticsEvents.CtaClick, {
        cta_type: ctaType,
        cta_label: ctaLabel,
      });
    },
    [trackEvent],
  );

  /** The viewer opened the Share link modal. */
  const trackShareOpen = useCallback(() => {
    trackEvent(MediaAnalyticsEvents.ShareLinkOpen);
  }, [trackEvent]);

  /** The viewer copied the share link, or opened a social/email share deep link. */
  const trackShareIntent = useCallback(
    (channel: 'copy' | ShareChannel) => {
      trackEvent(MediaAnalyticsEvents.ShareIntent, { channel });
    },
    [trackEvent],
  );

  // How the tutorial modal was last opened; null until it opens once.
  const tutorialAutoOpenedRef = useRef<boolean | null>(null);

  /** The viewer clicked the header Tutorial button. */
  const trackTutorialOpen = useCallback(() => {
    tutorialAutoOpenedRef.current = false;
    trackEvent(MediaAnalyticsEvents.TutorialOpen, { auto_opened: false });
  }, [trackEvent]);

  /**
   * The tutorial opened by itself on the first visit. No-op once the tutorial
   * has opened, so an effect re-running it is harmless.
   */
  const trackTutorialAutoOpen = useCallback(() => {
    if (tutorialAutoOpenedRef.current !== null) return;
    tutorialAutoOpenedRef.current = true;
    trackEvent(MediaAnalyticsEvents.TutorialOpen, { auto_opened: true });
  }, [trackEvent]);

  /** The viewer closed the tutorial modal. */
  const trackTutorialClose = useCallback(() => {
    trackEvent(MediaAnalyticsEvents.TutorialClose, {
      auto_opened: tutorialAutoOpenedRef.current ?? false,
    });
  }, [trackEvent]);

  /** The viewer submitted the first answer of an attempt in this visit. */
  const trackQuizStart = useCallback(
    (start: QuizStartEvent) => {
      trackEvent(MediaAnalyticsEvents.QuizStart, { resume: start.resume });
    },
    [trackEvent],
  );

  /** The viewer answered a quiz question. */
  const trackQuizAnswer = useCallback(
    (answer: QuizAnswerEvent) => {
      trackEvent(MediaAnalyticsEvents.QuizAnswer, toAnswerRecord(answer));
    },
    [trackEvent],
  );

  /** The viewer finished a quiz attempt. */
  const trackQuizAttempt = useCallback(
    (attempt: QuizAttemptEvent) => {
      trackEvent(MediaAnalyticsEvents.QuizAttempt, {
        answers: attempt.answers.map(toAnswerRecord),
        score: attempt.score,
        total_correct: attempt.totalCorrect,
        // Hardcoded until the backend exposes an endpoint to read prior
        // attempts. It must be server-assigned then — never client-guessed.
        // `attempt_number` is deliberately not sent: multiple attempts per
        // viewer aren't supported yet, so a client-side value would be noise.
        attempt_id: 1,
      });
    },
    [trackEvent],
  );

  // Grouped to match the Quiz add-on's single `analyticEvents` prop — pass
  // straight through: `<Quiz analyticEvents={quizAnalyticEvents} />`.
  const quizAnalyticEvents = useMemo<QuizAnalyticEvents>(
    () => ({
      onStart: trackQuizStart,
      onAnswer: trackQuizAnswer,
      onComplete: trackQuizAttempt,
    }),
    [trackQuizStart, trackQuizAnswer, trackQuizAttempt],
  );

  /** A Quick recall card became the visible card. */
  const trackQuickRecallReveal = useCallback(
    (itemIndex: number, totalItems: number) => {
      trackEvent(MediaAnalyticsEvents.QuickRecallItemReveal, {
        item_index: itemIndex,
        total_items: totalItems,
      });
    },
    [trackEvent],
  );

  /** The viewer flipped a Quick recall card to its answer. */
  const trackQuickRecallFlip = useCallback(
    (itemIndex: number, flipCount: number) => {
      trackEvent(MediaAnalyticsEvents.QuickRecallItemFlip, {
        item_index: itemIndex,
        flip_count: flipCount,
      });
    },
    [trackEvent],
  );

  /** The viewer stepped through the deck with Prev/Next. */
  const trackQuickRecallNavigate = useCallback(
    (direction: QuickRecallNavDirection, itemIndex: number) => {
      trackEvent(MediaAnalyticsEvents.QuickRecallItemNavigate, {
        item_index: itemIndex,
        direction,
      });
    },
    [trackEvent],
  );

  /** The viewer self-rated a card ("I knew this" / "New to me"). */
  const trackQuickRecallRate = useCallback(
    (itemIndex: number, rating: QuickRecallMark) => {
      trackEvent(MediaAnalyticsEvents.QuickRecallItemRate, {
        item_index: itemIndex,
        rating,
      });
    },
    [trackEvent],
  );

  // Grouped to match the QuickRecall add-on's `analyticEvents` prop — pass
  // straight through: `<QuickRecall analyticEvents={quickRecallAnalyticEvents} />`.
  const quickRecallAnalyticEvents = useMemo<QuickRecallAnalyticEvents>(
    () => ({
      onReveal: trackQuickRecallReveal,
      onFlip: trackQuickRecallFlip,
      onNavigate: trackQuickRecallNavigate,
      onRate: trackQuickRecallRate,
    }),
    [
      trackQuickRecallReveal,
      trackQuickRecallFlip,
      trackQuickRecallNavigate,
      trackQuickRecallRate,
    ],
  );

  /** The viewer expanded a key point in the Key points accordion. */
  const trackKeyPointsItemExpand = useCallback(
    (itemIndex: number, totalItems: number) => {
      trackEvent(MediaAnalyticsEvents.KeyPointsItemExpand, {
        item_index: itemIndex,
        total_items: totalItems,
      });
    },
    [trackEvent],
  );

  // Grouped to match the Key points add-on's `analyticEvents` prop — pass
  // straight through: `<Summary analyticEvents={summaryAnalyticEvents} />`.
  // The component itself is still exported as `Summary` (see libs/ui).
  const summaryAnalyticEvents = useMemo<SummaryAnalyticEvents>(
    () => ({
      onItemExpand: trackKeyPointsItemExpand,
    }),
    [trackKeyPointsItemExpand],
  );

  /** The viewer clicked a standout row (jumps to its document mark). */
  const trackStandoutsItemClick = useCallback(
    (itemIndex: number, totalItems: number) => {
      trackEvent(MediaAnalyticsEvents.StandoutsItemClick, {
        item_index: itemIndex,
        total_items: totalItems,
      });
    },
    [trackEvent],
  );

  // Grouped to match the Standouts add-on's `analyticEvents` prop — pass
  // straight through: `<Standouts analyticEvents={standoutsAnalyticEvents} />`.
  const standoutsAnalyticEvents = useMemo<StandoutsAnalyticEvents>(
    () => ({
      onItemClick: trackStandoutsItemClick,
    }),
    [trackStandoutsItemClick],
  );

  // Audio playback events. One grouped object (memoized on trackEvent only)
  // rather than seven useCallbacks — the callbacks share the same shape and
  // change together.
  const audioAnalyticEvents = useMemo<AudioAnalyticEvents>(
    () => ({
      onPlay: (info) =>
        trackEvent(MediaAnalyticsEvents.PodcastPlay, toPodcastProperties(info)),
      onPause: (info) =>
        trackEvent(
          MediaAnalyticsEvents.PodcastPause,
          toPodcastProperties(info),
        ),
      onSkip: (direction, info) =>
        trackEvent(
          direction === 'forward'
            ? MediaAnalyticsEvents.PodcastForward
            : MediaAnalyticsEvents.PodcastRewind,
          toPodcastProperties(info),
        ),
      onSpeedChange: (speed, info) =>
        trackEvent(MediaAnalyticsEvents.PodcastSpeed, {
          ...toPodcastProperties(info),
          speed,
        }),
      onSeek: (seekFromSeconds, seekToSeconds, info) =>
        trackEvent(MediaAnalyticsEvents.PodcastSeek, {
          ...toPodcastProperties(info),
          seek_from: Math.round(seekFromSeconds),
          seek_to: Math.round(seekToSeconds),
        }),
      onComplete: (info) =>
        trackEvent(
          MediaAnalyticsEvents.PodcastComplete,
          toPodcastProperties(info),
        ),
      onHeartbeat: (info) =>
        trackEvent(
          MediaAnalyticsEvents.PodcastHeartbeat,
          toPodcastProperties(info),
        ),
    }),
    [trackEvent],
  );

  return {
    trackAddonTabOpen,
    trackInitialAddonTabOpen,
    trackCtaClick,
    trackShareOpen,
    trackShareIntent,
    trackTutorialOpen,
    trackTutorialAutoOpen,
    trackTutorialClose,
    quizAnalyticEvents,
    quickRecallAnalyticEvents,
    summaryAnalyticEvents,
    standoutsAnalyticEvents,
    audioAnalyticEvents,
  };
}
